import { computed, Ref, ref } from 'vue';
import {
    Certificate,
    ClimateEmissionCollection,
    CustomizerModel,
    LightingSpecification,
    ProductDetailCoverMaterial,
    ProductDetailModel,
    ProductDetailTransparencyModel,
    ProductLabelType,
} from '@/types/productServerApi';
import useProductSpecificationsData from '@/shared/product/details/ui/product-specifications/useProductSpecificationsData';
import { CollageTileLayoutData } from '@/shared/content/blocks/collage-layouts/collage-layout.store';
import { useModuleConfigurator } from './ui/module-configurator/useModuleConfigurator';

const data: Ref<ProductDetailModel> = ref({} as ProductDetailModel);

const environmentalImages: Ref<CollageTileLayoutData[]> = ref([] as CollageTileLayoutData[]);

export default function useProductDetailData() {
    const { setSpecificationData } = useProductSpecificationsData();
    const { moduleConfiguratorTileData } = useModuleConfigurator();

    const setProductData = (value: ProductDetailModel) => {
        data.value = value;
        setSpecificationData(data.value.productId, data.value.id, data.value.specifications, data.value.coverMaterial, data.value.climateEmissionCollection);
    };

    const clearProductData = () => {
        data.value = {} as ProductDetailModel;
        setSpecificationData('', '', undefined, undefined, undefined);
    };

    const productData = computed<ProductDetailModel>(() => data.value ?? {} as ProductDetailModel);
    const productId = computed<string>(() => data.value?.productId ?? '');
    const variantId = computed<string>(() => data.value?.id ?? '');
    const productName = computed<string | undefined>(() => data.value?.name);
    const familyName = computed<string>(() => data.value.familyName ?? '');
    const lightingSpecification = computed<LightingSpecification | null>(() => data.value.lightingSpecification ?? null);
    const climateEmissionsCollection = computed<ClimateEmissionCollection | undefined>(() => data.value?.climateEmissionCollection);
    const customizer = computed<CustomizerModel>(() => data.value.customizerModel ?? {} as CustomizerModel);
    const isNewArrival = computed<boolean | undefined>(() => data.value?.productLabels?.some((label) => label.type === ProductLabelType.NewArrival));
    const productCertificationPrimary = computed<Certificate | undefined>(() => {
        return data.value?.specifications?.certificates?.find((certification) => certification && certification.imageUrl);
    });
    const coverMaterial = computed<ProductDetailCoverMaterial | undefined>(() => data.value?.coverMaterial);
    const productAddOnCount = computed<number>(() => data.value?.productAddOnCount);
    const transparencyData = computed<ProductDetailTransparencyModel | undefined>(() => data.value.productTransparencyDetails ?? undefined);

    const setEnvironmentImages = (tiles:CollageTileLayoutData[]) => {
        environmentalImages.value = tiles;
    };

    return {
        setProductData,
        clearProductData,
        productData,
        productId,
        variantId,
        productName,
        familyName,
        productCertificationPrimary,
        lightingSpecification,
        customizer,
        climateEmissionsCollection,
        isNewArrival,
        coverMaterial,
        productAddOnCount,
        environmentalImages,
        moduleConfiguratorTileData,
        setEnvironmentImages,
        transparencyData,
    };
}
